import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { userColor, userInitials } from '../theme/userColor';
import useUsers from '../hooks/useUsers';

// item 153 — a user's at-a-glance identity: a colored initials avatar (deterministic
// from userId via userColor) + their name. Reused anywhere a user appears (chat,
// commissions ledger, audit "by" fields, assignment pickers).
const SIZES = {
  sm: { box: '20px', font: '2xs' },
  md: { box: '28px', font: 'xs' },
  lg: { box: '36px', font: 'sm' },
} as const;

const UserChip: React.FC<{
  userId?: string | null;
  name?: string | null;
  size?: keyof typeof SIZES;
  showName?: boolean;
  // item 318 — profile picture (data URL); falls back to the colored initials avatar
  photoUrl?: string | null;
}> = ({
  userId, name, size = 'md', showName = true, photoUrl,
}) => {
  const color = userColor(userId);
  const s = SIZES[size];
  // item 572 — callers rarely pass photoUrl, so profile pictures were missing on whole
  // pages (Timesheets etc.). Resolve it HERE from the cached users list; the prop stays
  // as an override for rows that already carry the photo.
  const { data: usersData } = useUsers();
  const resolvedPhoto = photoUrl
    ?? (userId ? usersData?.index?.find((u) => u.id === userId)?.photoUrl : null);
  return (
    <Flex align="center" gap={2} minW={0}>
      <Flex
        flexShrink={0}
        w={s.box}
        h={s.box}
        // item 175 — explicit right margin: Chakra 1.6 doesn't reliably apply the parent
        // `gap`, so the initials avatar was jammed against the name
        mr={2}
        align="center"
        justify="center"
        borderRadius="full"
        bg={resolvedPhoto ? undefined : color.bg}
        color={color.fg}
        fontSize={s.font}
        fontWeight="bold"
        overflow="hidden"
        backgroundImage={resolvedPhoto ? `url(${resolvedPhoto})` : undefined}
        backgroundSize="cover"
        backgroundPosition="center"
        title={name ?? undefined}
      >
        {!resolvedPhoto && userInitials(name)}
      </Flex>
      {showName && (
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{name ?? 'Unknown'}</Text>
      )}
    </Flex>
  );
};

UserChip.defaultProps = {
  userId: undefined,
  name: undefined,
  size: 'md',
  showName: true,
  photoUrl: undefined,
};

export default UserChip;
